import React from 'react';
import { usePurchaseSummaryQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { PurchaseSummary } from './purchaseSummaryMeta';

/**
 * Consumer contract for the `PurchaseSummary` widget. Wraps
 * `usePurchaseSummaryQuery` (`/purchases/summary`) in the same
 * `{ data, error, isLoading, onRetry }` shape as `useRevenueSummary`, so the
 * container + ErrorBoundary wiring is interchangeable.
 */
export const usePurchaseSummary = () => {
    const query = usePurchaseSummaryQuery();

    const data = React.useMemo<PurchaseSummary | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    // Gate the error while a retry is in flight (see usePendingInvoices) so
    // the boundary's Retry doesn't re-throw the stale failed envelope.
    const error = React.useMemo<ErrorResponse | null>(
        () =>
            !query.isFetching && query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.isFetching, query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        data,
        error,
        isLoading:
            query.isPending || (query.isFetching && !query.data?.success),
        onRetry,
    } as const;
};
